import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card, { CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Button from '../../components/ui/Button';
import {
  Activity, Camera, AlertCircle, MapPin, Users, Video, Clock, BarChart3,
  RefreshCw, ArrowUpRight, ArrowRight, ShieldCheck,
} from 'lucide-react';
import { 
  cameraService, 
  locationService, 
  eventService, 
  visitorLogService
} from '../../services';
import { Event } from '../../types';

interface DashboardStats {
  totalCameras: number;
  activeCameras: number;
  totalLocations: number;
  eventsToday: number;
  totalEvents: number;
  unacknowledgedEvents: number;
  visitorLogsToday: number;
  totalVisitorLogs: number;
  activeAlarms: number;
}

interface EventTypeCount {
  type: string;
  count: number;
  color: string;
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalCameras: 0,
    activeCameras: 0,
    totalLocations: 0,
    eventsToday: 0,
    totalEvents: 0,
    unacknowledgedEvents: 0,
    visitorLogsToday: 0,
    totalVisitorLogs: 0,
    activeAlarms: 0,
  });
  const [recentEvents, setRecentEvents] = useState<Event[]>([]);
  const [eventTypeDistribution, setEventTypeDistribution] = useState<EventTypeCount[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  useEffect(() => {
    fetchDashboardData();
    
    // Auto-refresh every 2 minutes (only if user is active on dashboard)
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchDashboardData();
      }
    }, 120000); // 2 minutes

    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Prevent fetching if we just fetched in the last 30 seconds (debounce)
      const now = Date.now();
      if (now - lastFetchTime < 30000) {
        return;
      }
      setLastFetchTime(now);

      setLoading(true);
      setError(null);

      /**
       * PERFORMANCE OPTIMIZATION:
       * 
       * Instead of fetching all records (size=1000), we use smart strategies:
       * 1. Fetch size=1 to get totalElements count (counts without data transfer)
       * 2. Fetch small samples (size=15-50) for displays and calculations
       * 3. Use API's totalElements field for accurate total counts
       * 4. Debounce: Prevent requests within 30 seconds
       * 5. Auto-refresh: Only when page is visible (every 2 minutes)
       * 
       * This reduces server load from ~5000 records to ~100 records per refresh!
       * Old: 1000+1000+1000+dynamic+1000 = ~4000-5000 records
       * New: 50+1+15+dynamic+1+1 = ~70-100 records (98% reduction!)
       */
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];
      
      // End of today (23:59:59) for proper date range filtering
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      const todayEndStr = todayEnd.toISOString().replace('T', ' ').substring(0, 19);

      const [
        camerasResponse,
        locationsCountResponse,
        recentEventsResponse,
        todayEventsResponse,
        unacknowledgedEventsResponse,
        visitorLogsCountResponse,
      ] = await Promise.all([
        // Get camera count and small sample to calculate active cameras (size=50 max)
        cameraService.getCameras({ page: 1, size: 50 }),
        // Get total location count (size=1 to minimize data transfer)
        locationService.getLocations({ page: 1, size: 1 }),
        // Get only 15 most recent events for display
        eventService.getEvents({ page: 1, size: 15 }),
        // Get today's events count (start of day to end of day)
        eventService.getEventsByDateRange(todayStr, todayEndStr).catch(() => ({ data: [], totalElements: 0 })),
        // Get unacknowledged events (usually small number)
        eventService.getUnacknowledgedEvents(),
        // Get visitor logs count (size=1 to minimize data transfer)
        visitorLogService.getVisitorLogs({ page: 1, size: 1 }),
      ]);

      // Use totalElements from API response for counts (no need to fetch all data!)
      const totalCameras = camerasResponse.totalElements || camerasResponse.data.length;
      const totalLocations = locationsCountResponse.totalElements || locationsCountResponse.data.length;
      const totalVisitorLogs = visitorLogsCountResponse.totalElements || visitorLogsCountResponse.data.length;
      
      // Calculate active cameras from sample (good estimate)
      const activeCameras = camerasResponse.data.filter(
        camera => camera.status === true
      ).length;

      // Calculate event type distribution from recent events (good enough sample)
      const typeCount = new Map<string, number>();
      recentEventsResponse.data.forEach(event => {
        if (event.event_type) {
          typeCount.set(event.event_type, (typeCount.get(event.event_type) || 0) + 1);
        }
      });

      const eventColors: Record<string, string> = {
        'motion_detection': '#3b82f6',
        'line_crossing': '#8b5cf6',
        'intrusion': '#ef4444',
        'face_detection': '#f59e0b',
        'license_plate': '#10b981',
        'tampering': '#dc2626',
        'video_loss': '#6b7280',
      };

      const distribution: EventTypeCount[] = Array.from(typeCount.entries())
        .map(([type, count]) => ({
          type: type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          count,
          color: eventColors[type] || '#6b7280',
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6);

      // Calculate today's visitor logs from total (estimate based on recent pattern)
      // If backend doesn't support date filtering, we can estimate or fetch a small sample
      const visitorLogsToday = 0; // TODO: Implement backend date filtering or estimate

      setStats({
        totalCameras,
        activeCameras,
        totalLocations,
        eventsToday: todayEventsResponse.totalElements || todayEventsResponse.data.length,
        totalEvents: recentEventsResponse.totalElements || recentEventsResponse.data.length,
        unacknowledgedEvents: unacknowledgedEventsResponse.data.length,
        visitorLogsToday,
        totalVisitorLogs,
        activeAlarms: unacknowledgedEventsResponse.data.filter(
          e => e.alarm_priority === 'high' || e.alarm_priority === 'critical'
        ).length,
      });

      setRecentEvents(recentEventsResponse.data);
      setEventTypeDistribution(distribution);
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTime;
    
    if (timeSinceLastFetch < 30000 && lastFetchTime > 0) {
      const secondsRemaining = Math.ceil((30000 - timeSinceLastFetch) / 1000);
      alert(`Please wait ${secondsRemaining} seconds before refreshing again to avoid server overload.`);
      return;
    }
    
    fetchDashboardData();
  };

  const getEventSeverity = (event: Event): 'HIGH' | 'MEDIUM' | 'LOW' => {
    if (event.alarm_priority === 'critical' || event.alarm_priority === 'high') {
      return 'HIGH';
    }
    if (event.event_type === 'intrusion' || event.event_type === 'tampering') {
      return 'HIGH';
    }
    if (event.alarm_priority === 'medium' || event.event_type === 'line_crossing') {
      return 'MEDIUM';
    }
    return 'LOW';
  };

  const getSeverityVariant = (severity: string) => {
    switch (severity) {
      case 'HIGH':
        return 'danger';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatTimeAgo = (dateString?: string): string => {
    if (!dateString) return 'Unknown';
    
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  const formatEventType = (type?: string): string => {
    if (!type) return 'Unknown Event';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <Loading size="lg" text="Loading dashboard..." />
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <Card className="max-w-md">
            <CardContent>
              <div className="text-center">
                <AlertCircle className="mx-auto h-12 w-12 text-status-danger mb-4" />
                <h3 className="text-lg font-medium text-text-primary mb-2">Error Loading Dashboard</h3>
                <p className="text-text-muted mb-4">{error}</p>
                <Button onClick={fetchDashboardData}>Retry</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const metrics = [
    { label: 'Total cameras', value: stats.totalCameras, detail: `${stats.activeCameras} active`, icon: Camera, path: '/cameras', color: 'text-accent-primary', background: 'bg-accent-primary/10' },
    { label: 'Locations', value: stats.totalLocations, detail: 'Sites monitored', icon: MapPin, path: '/locations', color: 'text-blue-500', background: 'bg-blue-500/10' },
    { label: 'Events today', value: stats.eventsToday, detail: `${stats.totalEvents} total events`, icon: Activity, path: '/events', color: 'text-amber-500', background: 'bg-amber-500/10' },
    { label: 'Pending alerts', value: stats.unacknowledgedEvents, detail: `${stats.activeAlarms} high priority`, icon: AlertCircle, path: '/events', color: 'text-status-danger', background: 'bg-status-danger/10' },
  ];
  const sampledEvents = eventTypeDistribution.reduce((total, item) => total + item.count, 0);

  return (
    <MainLayout>
      <div className="page-stack">
        <div className="page-header">
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-description">Your security operations at a glance.</p>
          </div>
          <div className="page-actions">
            <Button variant="outline" onClick={handleRefresh} leftIcon={<RefreshCw size={15} />}>Refresh</Button>
            <Button onClick={() => navigate('/live')} leftIcon={<Video size={16} />}>Open Live View</Button>
          </div>
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 lg:gap-4">
          {metrics.map(({ label, value, detail, icon: Icon, path, color, background }) => (
            <Link key={label} to={path} className="group bg-surface-primary border border-border-default rounded-card shadow-card p-4 lg:p-5 text-left hover:border-accent-primary/40 transition-colors min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-text-secondary">{label}</span>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${color} ${background}`}><Icon size={17} /></span>
              </div>
              <p className="mt-3 text-3xl font-semibold tracking-tight text-text-primary tabular-nums">{value.toLocaleString()}</p>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-xs text-text-muted">{detail}</span>
                <ArrowUpRight size={14} className="text-text-muted group-hover:text-accent-primary shrink-0" />
              </div>
            </Link>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-text-secondary px-1">
          <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-accent-primary" />{stats.activeCameras} of {stats.totalCameras} cameras active</span>
          <span className="flex items-center gap-2"><Users size={14} className="text-text-muted" />{stats.totalVisitorLogs.toLocaleString()} visitor records</span>
          <span className="flex items-center gap-2"><Clock size={14} className="text-text-muted" />Updates every 2 minutes</span>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-5 items-start">
          <Card padding="none" className="xl:col-span-2 overflow-hidden">
            <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-0.5 px-5 py-4 border-b border-border-default">
              <h2 className="text-sm font-semibold text-text-primary">Recent events</h2>
              <Link to="/events" className="inline-flex min-h-6 items-center gap-1.5 text-xs font-medium text-accent-primary hover:underline">View all<ArrowRight size={14} /></Link>
              <p className="col-span-2 text-xs text-text-muted">Latest activity across your locations</p>
            </div>
            {recentEvents.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-20 text-text-muted">
                <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-surface-secondary mb-1"><Activity size={24} /></span>
                <p className="text-sm font-medium text-text-secondary">No recent events</p>
                <p className="text-xs">New camera activity will appear here.</p>
              </div>
            ) : (
              <div className="max-h-[460px] overflow-y-auto divide-y divide-border-default/60">
                {recentEvents.map(event => {
                  const severity = getEventSeverity(event);
                  return (
                    <Link key={event.event_id} to="/events" className="w-full flex min-h-11 items-center gap-3 px-4 sm:px-5 py-2 text-left hover:bg-surface-hover transition-colors">
                      <span className="hidden sm:flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border-default bg-surface-secondary text-text-secondary"><Activity size={16} /></span>
                      <span className="flex-1 min-w-0">
                        <span className="block truncate text-sm font-medium text-text-primary">{formatEventType(event.event_type)}</span>
                        <span className="block truncate text-xs text-text-muted mt-0.5">{event.location_name || event.camera_name || 'Unknown Location'}</span>
                      </span>
                      <span className="flex flex-col sm:flex-row items-end sm:items-center gap-1.5 sm:gap-3 shrink-0">
                        <span className="text-xs text-text-muted">{formatTimeAgo(event.created_date)}</span>
                        <Badge variant={getSeverityVariant(severity)} size="sm" dot>{severity.charAt(0) + severity.slice(1).toLowerCase()}</Badge>
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </Card>

          {/* One widget region shared by all viewport sizes. */}
          <aside aria-label="Dashboard widgets" className="space-y-4 lg:space-y-5 min-w-0">
            <Card padding="lg">
              <CardHeader>
                <div className="flex items-center justify-between gap-2"><CardTitle>Event distribution</CardTitle><BarChart3 size={17} className="text-text-muted" /></div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-text-muted mb-5">By type in the latest events</p>
                {eventTypeDistribution.length === 0 ? (
                  <p className="text-center text-sm text-text-muted py-10">No events recorded</p>
                ) : (
                  <div className="space-y-4">
                    {eventTypeDistribution.map(item => (
                      <div key={item.type}>
                        <div className="flex items-center justify-between gap-2 mb-2 text-xs">
                          <span className="text-text-secondary">{item.type}</span>
                          <span className="font-medium text-text-primary tabular-nums">{item.count}</span>
                        </div>
                        <div className="w-full bg-surface-secondary rounded-full h-1.5 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${sampledEvents ? (item.count / sampledEvents) * 100 : 0}%`, backgroundColor: item.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card padding="lg">
              <CardHeader><CardTitle>Quick access</CardTitle></CardHeader>
              <div className="space-y-1">
                {[
                  { label: 'Monitor live cameras', description: 'Open your camera workspace', icon: Video, path: '/live' },
                  { label: 'Manage locations', description: 'Sites, devices, and configuration', icon: MapPin, path: '/locations' },
                  { label: 'Entry / Exit logs', description: 'Review visitor activity', icon: Users, path: '/entry-exit' },
                ].map(({ label, description, icon: Icon, path }) => (
                  <Link key={path} to={path} className="w-full flex items-center gap-3 p-2.5 rounded-lg text-left hover:bg-surface-hover transition-colors">
                    <Icon size={17} className="shrink-0 text-text-muted" />
                    <span className="min-w-0 flex-1"><span className="block text-xs font-medium text-text-primary">{label}</span><span className="block text-xs text-text-muted mt-0.5">{description}</span></span>
                    <ArrowUpRight size={14} className="shrink-0 text-text-muted" />
                  </Link>
                ))}
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </MainLayout>
  );
};

export default DashboardPage;
